/**
 * @typedef {object} ColumnMapping
 * @property {number} colIndex
 * @property {string} effectName - Raw effect-band name from the file.
 * @property {string} questionName - Raw question header from the file.
 * @property {{id:string,name:string}|null} effect - Currently linked catalog effect.
 * @property {{id:string,name:string,posNeg?:boolean}|null} question - Currently linked catalog question.
 *
 * @typedef {object} CatalogEffect
 * @property {string} id
 * @property {string} name
 * @property {string} category
 * @property {Array<{id:string,name:string,posNeg?:boolean}>} questions
 */

import { getSectorInfo } from '../../../helpers/meetstandaard/sectorCatalog';

/**
 * Editable review table: one row per imported question column, with a dropdown to confirm
 * or change which meetstandaard question it links to (grouped by effect). Unlinked columns
 * are skipped on save.
 *
 * @param {object} props
 * @param {ColumnMapping[]} props.mappings - Current per-column mappings.
 * @param {CatalogEffect[]} props.flatEffects - Flattened meetstandaard catalog for the dropdowns.
 * @param {(colIndex:number, effect:CatalogEffect|null, question:object|null) => void} props.onChange - Edit handler.
 */
const ImportMappingTable = ({ mappings, flatEffects, onChange }) => {
    // Sector-varianten dragen dezelfde effectnaam (bv. 2× "Fysieke gezondheid");
    // zonder sector-suffix zijn ze in de dropdown niet te onderscheiden en kiest
    // de gebruiker zomaar de verkeerde variant (inbox #81).
    const nameCounts = flatEffects.reduce((acc, e) => {
        acc[e.name] = (acc[e.name] || 0) + 1;
        return acc;
    }, {});
    const effectLabel = (effect) => {
        if ((nameCounts[effect.name] || 0) < 2) return effect.name;
        const sectors = (effect.sectors || []).map((slug) => getSectorInfo(slug).label).join(', ');
        return sectors ? `${effect.name} — ${sectors}` : effect.name;
    };

    const handleSelect = (colIndex, value) => {
        if (!value) {
            onChange(colIndex, null, null);
            return;
        }
        const [effectId, questionId] = value.split('::');
        const effect = flatEffects.find((e) => e.id === effectId) || null;
        const question = effect ? effect.questions.find((q) => q.id === questionId) || null : null;
        onChange(colIndex, effect, question);
    };

    return (
        <table className="import-mapping-table">
            <thead>
                <tr>
                    <th>KOLOM IN BESTAND</th>
                    <th>GEKOPPELD AAN (MEETSTANDAARD)</th>
                </tr>
            </thead>
            <tbody>
                {mappings.map((m) => {
                    const value = m.question && m.effect ? `${m.effect.id}::${m.question.id}` : '';
                    const unmatched = !value;
                    return (
                        <tr key={m.colIndex} className={unmatched ? 'import-mapping-row-unmatched' : ''}>
                            <td>
                                <span className="import-mapping-effect-band">{m.effectName || '—'}</span>
                                <span className="import-mapping-question-label">{m.questionName}</span>
                            </td>
                            <td>
                                <select value={value} onChange={(e) => handleSelect(m.colIndex, e.target.value)}>
                                    <option value="">— Niet importeren —</option>
                                    {flatEffects.map((effect) => (
                                        <optgroup key={effect.id} label={effectLabel(effect)}>
                                            {effect.questions.map((q) => (
                                                <option key={q.id} value={`${effect.id}::${q.id}`}>
                                                    {q.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

export default ImportMappingTable;
